const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/appointment/AppointmentsPage.tsx';
import { PageLayout } from '../../layout/PageLayout';
import { AppBarMain } from '../DashboardPage/AppBar/AppBar';
import { EmptyState } from 'App/components/EmptyState/EmptyState';
import textMap from 'App/pages/appointment/translations';
import LoadingSpinner from '@cistec/common_web/components/LoadingSpinner';
import { AppointmentList } from 'App/components/AppointementList/AppointmentList';
import { useIntl } from 'react-intl';
import GoBackToDashboardButton from 'App/pages/GoBackToDashboardButton';
import * as React from 'react';
import { useMemo } from 'react';
import { css } from '@emotion/css';
import { useTheme } from '@mui/material/styles';
import { useFeatureFlags } from 'App/store/featureFlags';
import { useFutureAppointments, usePastAppointments } from 'App/store/appointment';
import { sortObjectsByPath } from '@cistec/common_web/utils/data';
import moment from 'moment';
import { KeyValuePair } from '@cistec/common_web/defs/generalDefs';
import { StructuredAppointment } from '@cistec/common_web/defs/kisim';

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            pastAppointments: css`
                margin-top: ${theme.spacing(5)};
            `,
        }),
        [theme]
    );
}

export const AppointmentsPage = (_props: {}) => {
    const { formatMessage } = useIntl();
    const classes = useCSSClasses();
    const { data: featureFlags } = useFeatureFlags();

    const { data: futureAppointments, isPendingOrLoading: isLoadingFutureAppointments } = useFutureAppointments();
    const { data: pastAppointments, isPendingOrLoading: isLoadingPastAppointments } = usePastAppointments();
    const isLoadingAppointments = isLoadingFutureAppointments || isLoadingPastAppointments;

    const { showPastAppointments } = featureFlags.appointment;

    const hasFutureAppointments = !!futureAppointments?.length;
    const hasPastAppointments = !!pastAppointments?.length;
    const hasAppointments = hasFutureAppointments || hasPastAppointments;

    const isEmptyState = !hasAppointments && !isLoadingAppointments;

    const groupByDay = (map: KeyValuePair, appointment: StructuredAppointment) => {
        const day = moment(appointment.STARTDAT).format('YYYYMMDD');
        map[day] = map[day] ?? [];
        map[day].push(appointment);
        return map;
    };

    const renderFutureAppointments = () => {
        if (isLoadingFutureAppointments) {
            return <LoadingSpinner center="alignSelf" delay={300} />;
        }
        if (hasFutureAppointments) {
            const appointmentMap = futureAppointments.reduce(groupByDay, {});
            const groupedAppointments = Object.keys(appointmentMap)
                .sort()
                .map(key => appointmentMap[key].sort(sortObjectsByPath('STARTDAT')));

            return (
                <AppointmentList
                    groupedAppointments={groupedAppointments}
                    title={formatMessage(textMap.upcomingAppointmentCardTitle)}
                />
            );
        }
    };

    const renderPastAppointments = () => {
        if (!showPastAppointments) return null;

        if (isLoadingPastAppointments) {
            return <LoadingSpinner center="alignSelf" delay={300} />;
        }
        if (hasPastAppointments) {
            const appointmentMap = pastAppointments.reduce(groupByDay, {});
            const groupedAppointments = Object.keys(appointmentMap)
                .sort()
                .reverse()
                .map(key => appointmentMap[key].sort(sortObjectsByPath('STARTDAT')));

            return (
                <AppointmentList
                    className={classes.pastAppointments}
                    groupedAppointments={groupedAppointments}
                    title={formatMessage(textMap.pastAppointmentCardTitle)}
                />
            );
        }
    };

    const renderPageContent = () => {
        if (isEmptyState) {
            return <EmptyState intlText={textMap.calendarEmpty} />;
        }
        return (
            <>
                <GoBackToDashboardButton />
                {renderFutureAppointments()}
                {renderPastAppointments()}
            </>
        );
    };

    return <PageLayout appBar={<AppBarMain />}>{renderPageContent()}</PageLayout>;
};
